import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { PAY_TYPES, normalizeDriverPay, canSetDriverPay } from '../../utils/driverPay';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Switch,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormHelperText, // Added Grid import
  Divider // Added Divider import
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
// Import permission functions directly from permissionStore
import permissionStore, { // Import the missing helper
  isManagementPrimaryRole
} from '../../utils/permissionStore'; // Corrected path
import { useNotification } from '../../context/NotificationContext';
import NotificationSettingsPanel from '../../components/common/NotificationSettingsPanel';
import { useOrg } from '../../context/OrgContext';

function EditUserDialog({ open, onClose, userToEdit, currentUser, availableRoles, regions, onUserUpdated }) {
  // Use notification hook
  const { showNotification } = useNotification();
  const { name: orgName } = useOrg();
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    personalAddressCountryCode: 'US',
    facilityAddressCountryCode: 'US'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false); // State for password change checkbox
  const [notificationPrefs, setNotificationPrefs] = useState(null); // State for notification preferences

  // Country mapping functions
  const getCountryCode = useCallback((countryValue) => {
    if (!countryValue) return 'US'; // Default to US
    
    // If it's already a code, return it
    if (['US', 'CA', 'MX'].includes(countryValue)) {
      return countryValue;
    }
    
    // Map full country names to codes
    const countryNameToCode = {
      'United States': 'US',
      'Canada': 'CA',
      'Mexico': 'MX'
    };
    
    return countryNameToCode[countryValue] || 'US'; // Default to US if not found
  }, []);

  const getCountryName = useCallback((countryCode) => {
    const countryCodeToName = {
      'US': 'United States',
      'CA': 'Canada',
      'MX': 'Mexico'
    };
    
    return countryCodeToName[countryCode] || 'United States'; // Default to United States
  }, []);

  useEffect(() => {
    console.log('📝 EditUserDialog: useEffect triggered', {
      userToEditId: userToEdit?.id,
      userName: userToEdit ? `${userToEdit.firstName} ${userToEdit.lastName}` : 'none',
      open,
      hasNotificationPreferences: !!userToEdit?.notificationPreferences,
      notificationPreferences: userToEdit?.notificationPreferences
    });
    
    if (userToEdit && open) {
      // Initialize form state when userToEdit changes
      const mappedUser = { ...userToEdit, newPassword: '', confirmPassword: '' };
      
      // Initialize formData with mapped country codes
      const newFormData = {
        personalAddressCountryCode: getCountryCode(mappedUser.address?.country),
        facilityAddressCountryCode: getCountryCode(mappedUser.facilityAddress?.country)
      };
      
      console.log('📝 EditUserDialog: Setting selectedUser with notification preferences:', {
        userId: mappedUser.id,
        notificationPreferences: mappedUser.notificationPreferences
      });
      
      setSelectedUser(mappedUser);
      setFormData(newFormData);
      setChangePassword(false); // Reset password change intention
      setError(null); // Clear errors
    } else if (!open) {
      setSelectedUser(null); // Clear state when dialog is closed
      setFormData({
        personalAddressCountryCode: 'US',
        facilityAddressCountryCode: 'US'
      });
    }
  }, [userToEdit, open, getCountryCode]); // Include getCountryCode in dependencies

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    // Phone fields accept any format and normalize to (XXX) XXX-XXXX
    const nextValue = type === 'checkbox'
      ? checked
      : (name === 'phone' || name === 'phoneNumber' ? formatPhoneNumber(value) : value);
    setSelectedUser(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  // Handler for notification preference changes from NotificationSettings component
  const handleNotificationPreferencesChange = (newPreferences) => {
    console.log('📝 EditUserDialog: Notification preferences changed:', newPreferences);
    setNotificationPrefs(newPreferences);
    // Also update selectedUser to include the new preferences for saving
    setSelectedUser(prev => ({
      ...prev,
      notificationPreferences: newPreferences
    }));
  };

  const handleAddressChange = (event) => {
    const { name, value } = event.target;
    
    // Handle country field specially to update formData
    if (name === 'country') {
      setFormData(prev => ({
        ...prev,
        personalAddressCountryCode: value
      }));
      // Also update selectedUser with the country code for consistency
      setSelectedUser(prev => ({
        ...prev,
        address: { ...(prev.address || {}), [name]: value }
      }));
    } else {
      setSelectedUser(prev => ({
        ...prev,
        address: { ...(prev.address || {}), [name]: value }
      }));
    }
  };

  const handleFacilityAddressChange = (event) => {
    const { name, value } = event.target;
    
    // Handle country field specially to update formData
    if (name === 'country') {
      setFormData(prev => ({
        ...prev,
        facilityAddressCountryCode: value
      }));
      // Also update selectedUser with the country code for consistency
      setSelectedUser(prev => ({
        ...prev,
        facilityAddress: { ...(prev.facilityAddress || {}), [name]: value },
        // Also update legacy fields if they exist in the form/state
        ...(name === 'country' && { facilityCountry: value }),
      }));
    } else {
      setSelectedUser(prev => ({
        ...prev,
        facilityAddress: { ...(prev.facilityAddress || {}), [name]: value },
        // Also update legacy fields if they exist in the form/state
        ...(name === 'street1' && { facilityAddress1: value }),
        ...(name === 'street2' && { facilityAddress2: value }),
        ...(name === 'city' && { facilityCity: value }),
        ...(name === 'state' && { facilityState: value }),
        ...(name === 'zip' && { facilityZip: value }),
      }));
    }
  };

  const handleSecondaryRoleChange = (event) => {
    const { name, checked } = event.target;
    setSelectedUser(prev => ({
      ...prev,
      secondaryRoles: { ...(prev.secondaryRoles || {}), [name]: checked }
    }));
  };

  const handleRegionChange = (event) => {
    const { value } = event.target;
    // Ensure we're setting an empty array when all regions are removed
    const newRegions = typeof value === 'string' ? value.split(',') : value;
    console.log("Setting regions to:", newRegions);
    setSelectedUser(prev => ({
      ...prev,
      regions: newRegions,
    }));
  };

  const handleToggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const canChangePassword = (selected, current) => {
    if (!selected || !current) return false;
    const isSameUser = (selected._id && current._id && selected._id === current._id);
    if (isSameUser) return true;
    const roleHierarchy = { 'N/A': 0, 'SP': 1, 'RM': 2, 'sOW': 3, 'OW': 4 };
    const currentRole = roleHierarchy[(current.primaryRole || current.role || 'N/A').toUpperCase()] || 0;
    const selectedRole = roleHierarchy[(selected.primaryRole || selected.role || 'N/A').toUpperCase()] || 0;
    return currentRole > selectedRole;
  };

  const canEditFacility = currentUser && (currentUser.primaryRole === 'OW' || currentUser.primaryRole === 'sOW');
  // Use permissionStore function for role editing capability
  const canModifySecondaryRoles = currentUser && selectedUser && permissionStore.canEditTargetUserRoles(currentUser, selectedUser);
  const isCurrentUserEditingSelf = currentUser && selectedUser && currentUser._id === selectedUser._id;
  // Determine if the editor is an SP editing someone within their vendor group
  const isSPEditingOwnVendorUser = currentUser?.primaryRole === 'SP' &&
                                  selectedUser &&
                                  (currentUser.vendorNumber === selectedUser.vendorNumber || currentUser.vendorId === selectedUser.vendorId);


  const handleSaveUser = async () => {
    if (!selectedUser) return;

    // Basic validation
    const basicFieldsValid = selectedUser.firstName && selectedUser.lastName && selectedUser.email && selectedUser.phone;
    
    // Address fields are only required for SP role
    const isSpRole = selectedUser.primaryRole === 'SP';
    const addressFieldsValid = !isSpRole || (
      selectedUser.address?.street1 && selectedUser.address?.city &&
      selectedUser.address?.state && selectedUser.address?.zip && selectedUser.address?.country
    );
    
    if (!basicFieldsValid || !addressFieldsValid) {
      const missingFields = [];
      if (!basicFieldsValid) missingFields.push('basic information (First Name, Last Name, Email, Phone)');
      if (isSpRole && !addressFieldsValid) missingFields.push('address information (required for SP role)');
      
      setError({ message: `Please fill in all required fields: ${missingFields.join(', ')}` });
      return;
    }
    if (changePassword && selectedUser.newPassword !== selectedUser.confirmPassword) {
        setError({ message: 'Passwords do not match.' });
        return;
    }
    if (changePassword && !selectedUser.newPassword) {
        setError({ message: 'New password cannot be empty if changing password.' });
        return;
    }
    
    // Validate SSN (Last 4) if provided
    if (selectedUser.ssnLast4 && selectedUser.ssnLast4.trim() !== '') {
      const ssnLast4Trimmed = selectedUser.ssnLast4.trim();
      if (!/^\d{4}$/.test(ssnLast4Trimmed)) {
        setError({ message: 'SSN (Last 4) must be exactly 4 digits' });
        return;
      }
    }


    try {
      setLoading(true);
      setError(null);

      const userData = { ...selectedUser };
      const userId = userData._id || userData.id; // Prefer _id

      if (!userId) {
        throw new Error('User ID is missing');
      }

      // Convert country codes back to full names for backend storage
      if (formData.personalAddressCountryCode) {
        userData.address = userData.address || {};
        userData.address.country = getCountryName(formData.personalAddressCountryCode);
      }
      
      // Also handle facility address if it exists
      if (formData.facilityAddressCountryCode) {
        userData.facilityAddress = userData.facilityAddress || {};
        userData.facilityAddress.country = getCountryName(formData.facilityAddressCountryCode);
      }

      // Only include password if the change checkbox is checked and password is provided
      if (changePassword && userData.newPassword) {
        userData.password = userData.newPassword;
      }
      // Remove temporary fields before sending
      delete userData.newPassword;
      delete userData.confirmPassword;

      // Ensure OW users keep their role
      if (userData.primaryRole === 'OW' || userData.role === 'OW') {
        userData.primaryRole = 'OW';
        userData.role = 'OW'; // Backward compatibility
      }

      // If editor is SP, ensure role wasn't changed (it shouldn't be possible via UI, but double-check)
      if (isSPEditingOwnVendorUser && userToEdit) {
          userData.primaryRole = userToEdit.primaryRole; // Force role back if somehow changed
          delete userData.regions; // SP cannot change regions
      }


      console.log(`Updating user ${userId} with data:`, userData);
      
      // DEBUG: Log notification preferences specifically
      console.log('DEBUG: EditUserDialog - Notification preferences in payload:', {
        notificationPreferences: userData.notificationPreferences,
        receiveEmailNotifications: userData.receiveEmailNotifications,
        receiveTextNotifications: userData.receiveTextNotifications,
        notifyOnNewJob: userData.notifyOnNewJob,
        notifyOnJobCanceled: userData.notifyOnJobCanceled,
        notifyOnJobOnScene: userData.notifyOnJobOnScene,
        notifyOnJobReassigned: userData.notifyOnJobReassigned,
        notifyOnJobMarkedAsGOA: userData.notifyOnJobMarkedAsGOA
      });

      let response;
      try {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, userData, { headers: getAuthHeader() });
      } catch (primaryError) {
        console.error('Error updating user with primary API path:', primaryError);
        if (primaryError.response?.status === 404) {
          response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, userData, { headers: getAuthHeader() });
        } else {
          throw primaryError;
        }
      }

      console.log('DEBUG: EditUserDialog - Updated user notificationPreferences:', response.data.notificationPreferences);
      
      console.log('📝 EditUserDialog: Calling onUserUpdated with updated user data:', {
        userId: response.data.id,
        hasNotificationPreferences: !!response.data.notificationPreferences,
        notificationPreferences: response.data.notificationPreferences
      });
      
      showNotification('User updated successfully!', 'success');
      onUserUpdated(response.data); // Pass updated user back
      onClose(); // Close dialog

    } catch (error) {
      console.error('Error updating user:', error);
      let errorMessage = 'Failed to update user. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
         errorMessage = 'Invalid data or validation error.';
      } else if (error.response?.status === 403) {
         errorMessage = 'You do not have permission to update this user.';
      }
      setError({ message: errorMessage });
      
      // Show notification for the error
      showNotification(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };


  if (!selectedUser) {
    return null; // Don't render if no user is selected
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit User: {selectedUser.firstName} {selectedUser.lastName}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error.message}
          </Typography>
        )}
        <TextField margin="dense" label="First Name *" name="firstName" fullWidth required value={selectedUser.firstName || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Last Name *" name="lastName" fullWidth required value={selectedUser.lastName || ''} onChange={handleInputChange} />
        <TextField
          margin="dense"
          label="SSN (Last 4)"
          name="ssnLast4"
          fullWidth
          value={selectedUser.ssnLast4 || ''}
          onChange={handleInputChange}
          inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
          helperText="Optional - Enter exactly 4 digits for de-duplication purposes"
        />
        <TextField margin="dense" label="Email *" name="email" type="email" fullWidth required value={selectedUser.email || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Phone *" name="phone" fullWidth required value={selectedUser.phone || ''} onChange={handleInputChange} />
        <TextField margin="dense" label="Username" name="username" fullWidth value={selectedUser.username || ''} onChange={handleInputChange} disabled /> {/* Username likely shouldn't be editable */}

        {/* Payout Method - how this user is paid for jobs. Drives the job's
            Payment Type when a job is assigned to them. Only Org 1 admins
            (or OW/sOW/RM) can change it; backend enforces this too. */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Payout Method</InputLabel>
          <Select
            name="payoutMethod"
            value={selectedUser.payoutMethod || 'ACH'}
            label="Payout Method"
            onChange={handleInputChange}
            disabled={!(currentUser?.organizationLevel === 1 && (currentUser?.secondaryRoles?.admin === true || ['OW', 'sOW', 'OWp', 'RM'].includes(currentUser?.primaryRole)))}
          >
            <MenuItem value="ACH">ACH</MenuItem>
            <MenuItem value="Credit Card">Credit Card</MenuItem>
          </Select>
        </FormControl>

        {/* Driver pay rate — what this driver earns per job. Org 1 admins set it for
            Org 1 drivers (the weekly ACH run pays by it); SP owners/admins set it
            for their own drivers. The backend strips it from anyone else. */}
        {(selectedUser.secondaryRoles?.driver || selectedUser.primaryRole === 'N/A') && (() => {
          const pay = normalizeDriverPay(selectedUser.driverPay);
          const canEdit = canSetDriverPay(currentUser, selectedUser);
          const setPay = (patch) => setSelectedUser((prev) => ({ ...prev, driverPay: { ...normalizeDriverPay(prev.driverPay), ...patch } }));
          return (
            <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2">Driver pay rate</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {selectedUser.organizationLevel === 1 ? 'Used by the weekly ACH payout for this driver.' : `The company's record of what it pays this driver; ${orgName} pays the company the job amount.`}
                {pay.updatedAt || selectedUser.driverPay?.updatedAt ? ` Last set ${new Date(selectedUser.driverPay.updatedAt).toLocaleDateString()}${selectedUser.driverPay.updatedBy ? ` by ${selectedUser.driverPay.updatedBy}` : ''}.` : ''}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }} disabled={!canEdit}>
                  <InputLabel>Rate type</InputLabel>
                  <Select label="Rate type" value={pay.type} onChange={(e) => setPay({ type: e.target.value })}>
                    {PAY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
                {pay.type === 'percent' && <TextField size="small" type="number" label="Percent of job" value={pay.percent} onChange={(e) => setPay({ percent: e.target.value })} inputProps={{ min: 0, max: 100, step: 0.5 }} disabled={!canEdit} sx={{ width: 150 }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />}
                {pay.type === 'flat' && <TextField size="small" type="number" label="Per job" value={pay.flatAmount} onChange={(e) => setPay({ flatAmount: e.target.value })} inputProps={{ min: 0, step: 0.01 }} disabled={!canEdit} sx={{ width: 150 }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />}
                <TextField size="small" label="Note (optional)" value={pay.note} onChange={(e) => setPay({ note: e.target.value })} inputProps={{ maxLength: 200 }} disabled={!canEdit} sx={{ flex: '1 1 200px' }} />
              </Box>
            </Box>
          );
        })()}

        {/* Password Change Section */}
        {canChangePassword(selectedUser, currentUser) && (
          <>
            <FormControlLabel
              control={<Checkbox checked={changePassword} onChange={(e) => setChangePassword(e.target.checked)} />}
              label="Change Password"
              sx={{ mt: 1 }}
            />
            {changePassword && (
              <>
                <TextField
                  margin="dense"
                  label="New Password"
                  name="newPassword"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={selectedUser.newPassword || ''}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleToggleShowPassword} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  margin="dense"
                  label="Confirm New Password"
                  name="confirmPassword"
                  fullWidth
                  type="password"
                  value={selectedUser.confirmPassword || ''}
                  onChange={handleInputChange}
                />
              </>
            )}
          </>
        )}

        {/* Role Selection */}
        {/* Conditionally render Role selection */}
        {(selectedUser.primaryRole === 'OW' || selectedUser.role === 'OW') ? (
          // Owner role cannot be changed
          <TextField margin="dense" label="Primary Role" fullWidth value="OW" disabled helperText="Owner role cannot be changed" />
        ) : !isSPEditingOwnVendorUser ? ( 
          // Show dropdown if editor is NOT SP editing own vendor user
          <FormControl fullWidth margin="dense"> 
            <InputLabel>Primary Role</InputLabel>
            <Select name="primaryRole" value={selectedUser.primaryRole || selectedUser.role || ''} onChange={handleInputChange}>
              {/* Ensure the user's current role is an option even if not creatable */}
              {(selectedUser.primaryRole && !availableRoles.includes(selectedUser.primaryRole)) &&
                 <MenuItem key={selectedUser.primaryRole} value={selectedUser.primaryRole}>{selectedUser.primaryRole}</MenuItem>
              }
              {availableRoles.map((role) => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null /* Hide role selection completely if SP editing own vendor user */}

        <Typography variant="subtitle1" sx={{ mt: 2 }}>Secondary Roles</Typography>
        {!canModifySecondaryRoles && !isCurrentUserEditingSelf && (
             <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
               You do not have permission to change secondary roles.
             </Typography>
        )}
         {isCurrentUserEditingSelf && (
             <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
               You cannot change your own secondary roles.
             </Typography>
         )}
        {/* Use canEditTargetUserRoles for disabling checkboxes */}
        <FormControlLabel control={<Checkbox name="admin" checked={selectedUser.secondaryRoles?.admin || false} onChange={handleSecondaryRoleChange} disabled={!canModifySecondaryRoles || isCurrentUserEditingSelf} />} label="Admin" />
        <FormControlLabel control={<Checkbox name="dispatcher" checked={selectedUser.secondaryRoles?.dispatcher || false} onChange={handleSecondaryRoleChange} disabled={!canModifySecondaryRoles || isCurrentUserEditingSelf} />} label="Dispatcher" />
        {isManagementPrimaryRole(selectedUser.primaryRole || selectedUser.role) ? (
          <FormControlLabel control={<Checkbox name="driver" checked disabled />} label="Driver (always on for management roles)" />
        ) : (
          <FormControlLabel control={<Checkbox name="driver" checked={selectedUser.secondaryRoles?.driver || false} onChange={handleSecondaryRoleChange} disabled={!canModifySecondaryRoles || isCurrentUserEditingSelf} />} label="Driver" />
        )}
        <FormControlLabel control={<Checkbox name="answeringService" checked={selectedUser.secondaryRoles?.answeringService || false} onChange={handleSecondaryRoleChange} disabled={!canModifySecondaryRoles || isCurrentUserEditingSelf} />} label="Answering Service" />

        {/* On Duty Switch */}
        {(selectedUser.secondaryRoles?.driver || selectedUser.isDriver || isManagementPrimaryRole(selectedUser.primaryRole || selectedUser.role)) && (
          <FormControlLabel
            control={<Switch name="isOnDuty" checked={selectedUser.isOnDuty || false} onChange={handleInputChange} />}
            label="On Duty"
            sx={{ display: 'block', mt: 1 }}
          />
        )}

        {/* Active Status Switch */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>Status</Typography>
         {/* Determine if the current user can toggle the active status */}
         {(() => {
             const isActive = selectedUser.isActive !== false;
             const canToggle = isActivating => isActivating
                  ? permissionStore.canActivateTargetUser(currentUser, selectedUser) // This function needs backend data (deactivatedByVendorId) to be fully correct later
                  : permissionStore.canDeactivateTargetUser(currentUser, selectedUser);
              const isDisabled = isCurrentUserEditingSelf || !canToggle(!isActive);
              let helperText = isCurrentUserEditingSelf ? "You cannot deactivate your own account." : "";
              // Simplified helper text - relies on isDisabled being correctly calculated by canActivate/Deactivate
              if (isDisabled && !isCurrentUserEditingSelf) {
                  helperText = "You do not have permission to change this user's status.";
              }
              // Note: Specific messages like "Only an Owner can reactivate..." are removed
              // as canActivateTargetUser should handle disabling the switch correctly.
              // Once backend provides deactivatedByVendorId, canActivateTargetUser needs update.

              return (
                 <>
                     <FormControlLabel
                       control={<Switch name="isActive" checked={isActive} onChange={handleInputChange} disabled={isDisabled} />}
                       label="Active"
                     />
                     {helperText && <FormHelperText error={!isActive}>{helperText}</FormHelperText>}
                 </>
             );
         })()}


        {/* Notification Preferences Section */}
        <Divider sx={{ my: 2 }} />
        <NotificationSettingsPanel
          user={selectedUser}
          onPreferencesChange={handleNotificationPreferencesChange}
          isAdminEditing={true}
          disabled={false}
        />


        {/* Personal Address */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Personal Address {selectedUser.primaryRole === 'SP' ? '*' : '(Optional)'}
        </Typography>
        <TextField
          margin="dense"
          label={`Street 1 ${selectedUser.primaryRole === 'SP' ? '*' : ''}`}
          name="street1"
          fullWidth
          required={selectedUser.primaryRole === 'SP'}
          value={selectedUser.address?.street1 || ''}
          onChange={handleAddressChange}
        />
        <TextField margin="dense" label="Street 2" name="street2" fullWidth value={selectedUser.address?.street2 || ''} onChange={handleAddressChange} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            margin="dense"
            label={`City ${selectedUser.primaryRole === 'SP' ? '*' : ''}`}
            name="city"
            fullWidth
            required={selectedUser.primaryRole === 'SP'}
            value={selectedUser.address?.city || ''}
            onChange={handleAddressChange}
          />
          <TextField
            margin="dense"
            label={`State ${selectedUser.primaryRole === 'SP' ? '*' : ''}`}
            name="state"
            fullWidth
            required={selectedUser.primaryRole === 'SP'}
            value={selectedUser.address?.state || ''}
            onChange={handleAddressChange}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            margin="dense"
            label={`ZIP ${selectedUser.primaryRole === 'SP' ? '*' : ''}`}
            name="zip"
            fullWidth
            required={selectedUser.primaryRole === 'SP'}
            value={selectedUser.address?.zip || ''}
            onChange={handleAddressChange}
          />
          <FormControl fullWidth margin="dense" required={selectedUser.primaryRole === 'SP'}>
            <InputLabel>{`Country ${selectedUser.primaryRole === 'SP' ? '*' : ''}`}</InputLabel>
            <Select name="country" value={formData.personalAddressCountryCode || 'US'} onChange={handleAddressChange}>
              <MenuItem value="US">United States</MenuItem>
              <MenuItem value="CA">Canada</MenuItem>
              <MenuItem value="MX">Mexico</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Facility Address - Only show for users who can edit facility */}
        {canEditFacility && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>Facility Address</Typography>
            <TextField margin="dense" label="Facility Street 1" name="street1" fullWidth value={selectedUser.facilityAddress?.street1 || ''} onChange={handleFacilityAddressChange} />
            <TextField margin="dense" label="Facility Street 2" name="street2" fullWidth value={selectedUser.facilityAddress?.street2 || ''} onChange={handleFacilityAddressChange} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField margin="dense" label="Facility City" name="city" fullWidth value={selectedUser.facilityAddress?.city || ''} onChange={handleFacilityAddressChange} />
              <TextField margin="dense" label="Facility State" name="state" fullWidth value={selectedUser.facilityAddress?.state || ''} onChange={handleFacilityAddressChange} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField margin="dense" label="Facility ZIP" name="zip" fullWidth value={selectedUser.facilityAddress?.zip || ''} onChange={handleFacilityAddressChange} />
              <FormControl fullWidth margin="dense">
                <InputLabel>Facility Country</InputLabel>
                <Select name="country" value={formData.facilityAddressCountryCode || 'US'} onChange={handleFacilityAddressChange}>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="MX">Mexico</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {/* Conditionally render Region Assignment only if editor is NOT SP and selected role is RM */}
        {!isSPEditingOwnVendorUser && selectedUser.primaryRole === 'RM' && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 2 }}>Region Assignment</Typography>
            <FormControl fullWidth margin="dense">
              <InputLabel>Assigned Region(s)</InputLabel>
              <Select
                multiple
                name="regions"
                value={selectedUser.regions || []}
                onChange={handleRegionChange}
                renderValue={(selected) => {
                  // Ensure selected is an array before filtering/mapping
                  const selectedIds = Array.isArray(selected) ? selected : [];
                  const selectedRegions = regions.filter(r => selectedIds.includes(r._id));
                  return selectedRegions.map(r => r.name).join(', ');
                }}
                >
                {regions.map((region) => (
                  <MenuItem key={region._id} value={region._id}>{region.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            </>
        )}
         {/* Removed duplicate helper text for SP editing region */}


      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSaveUser} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditUserDialog;
